import EventTickets from "./pages/EventTickets";
import StartupSurface from "./components/frontrow/StartupSurface";
import "@/App.css";
import { lazy, Suspense, useEffect, useLayoutEffect, useState, useCallback } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ClerkProvider } from "@clerk/clerk-react";

import { EntitlementGate, clearEntitlementCache } from "./components/billing/EntitlementGate";

import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import FamilyInvitationReturn from "./components/FamilyInvitationReturn";
import { SignInInner, SignUpInner, AuthShellLayout } from "./pages/SignIn";
import { FrontRowProvider } from "./context/FrontRowContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ensureDeviceNotifications } from "./lib/pushNotifications";
import { NativeScrollReset, resetNativeScrollAfterSignIn } from "./lib/nativeScrollReset";
import { loadNativeClerk } from "./lib/nativeClerk";
import { initialiseNativePushNavigation } from "./lib/nativePushNotifications";
import { startAppleTransactionSync, stopAppleTransactionSync } from "./lib/appleStore";

function NativeClerkProvider({ publishableKey, children }) {
  const isNative = window.location.protocol === "capacitor:";
  const [clerk, setClerk] = useState(null);
  const [failed, setFailed] = useState(false);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    if (!isNative) return;
    let cancelled = false;
    setFailed(false);

    loadNativeClerk(publishableKey).then(
      instance => {
        if (!cancelled) setClerk(instance);
      },
      () => {
        if (!cancelled) setFailed(true);
        console.error("FrontRow could not restore native sign-in.");
      }
    );

    return () => { cancelled = true; };
  }, [isNative, publishableKey, retry]);

  if (!isNative) {
    return (
      <ClerkProvider publishableKey={publishableKey}>
        {children}
      </ClerkProvider>
    );
  }

  if (failed) {
    return <ConfigError onRetry={() => setRetry(value => value + 1)} />;
  }
  if (!clerk) return <ConfigLoading />;

  return (
    <ClerkProvider
      publishableKey={publishableKey}
      Clerk={clerk}
      standardBrowser={false}
    >
      {children}
    </ClerkProvider>
  );
}

const loadFrontRow = () => import("./pages/FrontRow");
const FrontRow = lazy(loadFrontRow);
const EventDetail = lazy(() => import("./pages/EventDetail"));
const Settings = lazy(() => import("./pages/Settings"));
const AdminHealth = lazy(() => import("./pages/AdminHealth"));
const Billing = lazy(() => import("./pages/Billing"));
const BillingReturn = lazy(() => import("./pages/BillingReturn"));
const FamilyJoin = lazy(() => import("./pages/FamilyJoin"));
const ConnectEmail = lazy(() => import("./pages/ConnectEmail"));
const BrainTest = lazy(() => import("./pages/BrainTest"));
const EngineInspector = lazy(() => import("./pages/EngineInspector"));
const EmailTest = lazy(() => import("./pages/EmailTest"));
const EmailPipeline = lazy(() => import("./pages/EmailPipeline"));

// CASA AL1 remediation (2026-09-05): diagnostic pages are not part of the
// parent product and are hidden by default in every build. A developer must
// explicitly opt a non-production build in with REACT_APP_ENABLE_DEV_ROUTES=1.
const DEV_UI_ENABLED = process.env.REACT_APP_ENABLE_DEV_ROUTES === "1";

// 2026-08-29 · Legal pages must render on the production domain
// without Clerk sign-in, without the /api/config bootstrap, and
// without the AuthProvider gate — Google's OAuth verification team
// crawls these pages from unauthenticated user agents. We match the
// pathname before any of the auth machinery runs.
const PUBLIC_LEGAL_PATHS = new Set(["/privacy", "/terms"]);
const isPublicLegalPath = () => {
  if (typeof window === "undefined") return false;
  return PUBLIC_LEGAL_PATHS.has(window.location.pathname);
};

// 2026-09-13 · Privacy containment: older ConnectEmail builds stored the
// declared school email under one browser-wide localStorage key. That value
// could survive sign-out and be rendered to the next parent using the same
// browser. It is only transient onboarding input, so never trust or preserve
// the legacy browser copy across authenticated renders. The authoritative
// family-scoped value remains server-side.
const LEGACY_DECLARED_EMAIL_KEY = "frx_declared_email";
export const clearLegacyDeclaredEmail = () => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(LEGACY_DECLARED_EMAIL_KEY);
  } catch {
    // Storage can be unavailable in private/restricted browser contexts.
  }
};

const CONFIG_ERROR_COPY =
  "FrontRow couldn't load right now. Please check your connection and try again.";

function ConfigLoading() {
  return <StartupSurface testId="config-loading" />;
}

function ConfigError({ onRetry }) {
  return (
    <div
      data-testid="config-error"
      className="min-h-screen flex items-center justify-center bg-[#FAFAF8] px-6"
    >
      <div className="max-w-md text-center space-y-4">
        <p
          data-testid="config-error-message"
          className="text-[15px] text-[#1F2937]"
        >
          {CONFIG_ERROR_COPY}
        </p>
        <button
          type="button"
          data-testid="config-error-retry"
          onClick={onRetry}
          className="px-4 py-2 rounded-full bg-[#1F2937] text-white text-[14px] hover:bg-[#111827] transition-colors"
        >
          Try again
        </button>
      </div>
    </div>
  );
}

function AuthedRoutes() {
  const { status, verifyInFlight, refresh, familyId, clerkUser } = useAuth();
  useEffect(() => { if (status !== "signed_out") return; clearEntitlementCache(); }, [status]);

  useLayoutEffect(() => {
    if (window.location.protocol !== "capacitor:" || status !== "linked") return;
    return resetNativeScrollAfterSignIn();
  }, [status]);

  clearLegacyDeclaredEmail();

  useEffect(() => {
    initialiseNativePushNavigation().catch(() => {});
  }, []);

  useEffect(() => {
    // App Store renewals, approvals and interrupted purchases reach the server
    // only while a family is signed in (the server binds them to it).
    if (status !== "linked" || verifyInFlight) return undefined;
    startAppleTransactionSync().catch(() => {});
    return () => { stopAppleTransactionSync().catch(() => {}); };
  }, [status, verifyInFlight, familyId]);

  useEffect(() => {
    if (status !== "linked" || verifyInFlight) return;
    ensureDeviceNotifications().catch(() => {
      // Push is best-effort and must never block the parent dashboard.
    });
  }, [status, verifyInFlight, familyId, clerkUser?.id]);

  if (status === "checking") {
    return <StartupSurface testId="auth-checking" />;
  }
  if (verifyInFlight) {
    return <StartupSurface testId="auth-verifying" />;
  }
  if (status === "verify_error") {
    return <ConfigError onRetry={refresh} />;
  }
  if (status === "signed_out") {
    return (
      <Routes>
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/family/join" element={<FamilyJoin />} />
        <Route path="/" element={<Navigate to="/sign-in" replace />} />
        <Route path="/event/:id/tickets" element={<div className="mx-auto max-w-md px-5 py-8">
          <h1 className="mb-4 text-2xl font-semibold">Sign in to open your event tickets</h1>
          <SignInInner />
        </div>} />
        <Route element={<AuthShellLayout />}>
          <Route path="/sign-in/*" element={<SignInInner />} />
          <Route path="/sign-up/*" element={<SignUpInner />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }
  return (
    <Suspense fallback={<ConfigLoading />}>
    <FrontRowProvider>
      <Routes>
        <Route
          path="/"
          element={
            <FamilyInvitationReturn>
              <EntitlementGate>
                <FrontRow />
              </EntitlementGate>
            </FamilyInvitationReturn>
          }
        />
        <Route path="/billing" element={<Billing />} />
        <Route path="/billing/return" element={<BillingReturn />} />
        <Route path="/family/join" element={<FamilyJoin />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/connect-email" element={<ConnectEmail />} />
        <Route path="/event/:id/tickets" element={<EntitlementGate><EventTickets /></EntitlementGate>} />
        <Route path="/event/:id" element={<EntitlementGate><EventDetail /></EntitlementGate>} />
        <Route path="/settings" element={<EntitlementGate allowUnpaid><Settings /></EntitlementGate>} />
        <Route path="/admin" element={<AdminHealth />} />
        {DEV_UI_ENABLED && (
          <>
            <Route path="/dev/brain-test" element={<BrainTest />} />
            <Route path="/dev/engine-inspector" element={<EngineInspector />} />
            <Route path="/dev/email-test" element={<EmailTest />} />
            <Route path="/dev/email-pipeline" element={<EmailPipeline />} />
          </>
        )}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </FrontRowProvider>
    </Suspense>
  );
}

// Clerk's publishable key is public client configuration, not a secret. In
// production it should be injected into the frontend build so a returning PWA
// launch does not have to wake the backend merely to mount Clerk. Keep the
// existing /api/config path as a compatibility fallback for environments that
// have not yet supplied the build value. This changes only how Clerk receives
// its public key; AuthProvider's protected session/verify flow is untouched.
// Preview copies ask their own server, so a test-only Clerk app needs no shared key.
const IS_PREVIEW_BUILD = /\.preview\.emergentagent\.com/.test(
  process.env.REACT_APP_BACKEND_URL || ""
);
const BUILD_CLERK_PUBLISHABLE_KEY = IS_PREVIEW_BUILD
  ? ""
  : (process.env.REACT_APP_CLERK_PUBLISHABLE_KEY || "").trim();

function MainApp() {
  // Fetch code while Clerk/household checks run, rather than starting a new
  // download after they finish. This imports code only, never family data.
  useEffect(() => {
    if (window.location.pathname === "/") loadFrontRow().catch(() => {});
  }, []);
  const [status, setStatus] = useState(
    BUILD_CLERK_PUBLISHABLE_KEY ? "ready" : "loading"
  );
  const [publishableKey, setPublishableKey] = useState(
    BUILD_CLERK_PUBLISHABLE_KEY
  );

  const loadConfig = useCallback(async () => {
    // Normal production startup never enters this path once the public build
    // key is configured. It remains for local/legacy deployments and Retry.
    if (BUILD_CLERK_PUBLISHABLE_KEY) {
      setPublishableKey(BUILD_CLERK_PUBLISHABLE_KEY);
      setStatus("ready");
      return;
    }
    setStatus("loading");
    try {
      const r = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/config`,
        { credentials: "omit" }
      );
      if (!r.ok) throw new Error(`config_http_${r.status}`);
      const body = await r.json();
      const key = body && body.clerk_publishable_key;
      if (!key) throw new Error("config_missing_key");
      setPublishableKey(key);
      setStatus("ready");
    } catch (_err) {
      setStatus("error");
    }
  }, []);

  useEffect(() => {
    if (!BUILD_CLERK_PUBLISHABLE_KEY) loadConfig();
  }, [loadConfig]);

  if (status === "loading") return <ConfigLoading />;
  if (status === "error") return <ConfigError onRetry={loadConfig} />;

  return (
    <BrowserRouter>
      <NativeScrollReset />
      <NativeClerkProvider publishableKey={publishableKey}>
        <AuthProvider>
          <AuthedRoutes />
        </AuthProvider>
      </NativeClerkProvider>
    </BrowserRouter>
  );
}

function App() {
  if (isPublicLegalPath()) {
    return (
      <div className="App" data-testid="app-root">
        <BrowserRouter>
          <Routes>
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
          </Routes>
        </BrowserRouter>
      </div>
    );
  }
  return (
    <div className="App" data-testid="app-root">
      <MainApp />
    </div>
  );
}

export default App;
